import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WordReveal } from '@/components/ui/word-reveal';

export function Testimonials() {
  const testimonials = [
    {
      quote:
        'Filing company incorporation used to take weeks of paperwork. With EcRegi, our Associate handled everything via chat in under 7 days!',
      name: 'Priya Ananth',
      role: 'Founder at TechPulse',
      initials: 'PA',
      image: '/testimonial_bg_2.png',
      headline: 'Fast & hassle-free incorporation for growing startups.',
    },
    {
      quote:
        'EcRegi made our GST registration so easy. The process was smooth, communication was clear, and everything was completed on time.',
      name: 'Rohit Mehta',
      role: 'Small Business Owner',
      initials: 'RM',
      image: '/testimonial_bg.png',
      headline: "Focus on your business. We'll handle the compliance.",
    },
    {
      quote:
        'The transparent pricing and instant GST-compliant invoices made tax filing completely hassle-free. Highly recommended for growing businesses!',
      name: 'Vikram Verma',
      role: 'E-Commerce Seller',
      initials: 'VV',
      image: '/testimonial_bg_3.png',
      headline: '100% transparent pricing with zero hidden fees.',
    },
    {
      quote:
        'As a practicing CA, partnering with EcRegi allowed me to assist clients nationwide seamlessly. The platform tools are world-class.',
      name: 'Ananya Sharma',
      role: 'CA & Legal Consultant',
      initials: 'AS',
      image: '/testimonial_bg_4.png',
      headline: 'Dedicated expert CA & legal support in your language.',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6500);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const active = testimonials[currentIndex];

  return (
    <section
      id="benefits"
      className="scroll-mt-20 bg-white py-20 sm:py-28 border-t border-gray-100 relative"
    >
      <div className="mx-auto w-full max-w-7xl 2xl:max-w-[1400px] px-4 sm:px-6 lg:px-8">
        {/* Header section */}
        <div className="space-y-3 mb-10 sm:mb-14">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#15803d]">
            TESTIMONIALS
          </span>
          <WordReveal
            as="h2"
            text="What our clients say"
            className="font-display text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl"
            stagger={0.06}
          />
        </div>

        {/* 2-Column Grid */}
        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-12">
          {/* Left Review Card */}
          <div className="lg:col-span-6 rounded-2xl border border-gray-200/90 bg-white p-6 sm:p-9 flex flex-col justify-between space-y-6 min-h-[320px] overflow-hidden shadow-xs">
            <div className="relative">
              <span className="font-serif text-5xl text-[#15803d]/30 block mb-1 leading-none select-none">
                “
              </span>
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentIndex}
                  initial={{ opacity: 0, y: 12, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -12, filter: 'blur(4px)' }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="text-base sm:text-lg font-medium leading-relaxed text-gray-800"
                >
                  {active.quote}
                </motion.p>
              </AnimatePresence>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-5 border-t border-gray-100">
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="h-11 w-11 rounded-full bg-[#15803d] text-white font-extrabold flex items-center justify-center text-xs shrink-0 shadow-xs">
                  {active.initials}
                </div>
                <div className="min-w-0">
                  <h4 className="font-display text-sm font-bold text-gray-900 truncate">
                    {active.name}
                  </h4>
                  <p className="text-xs text-gray-500 truncate font-normal">{active.role}</p>
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                {/* Dots indicator */}
                <div className="flex items-center gap-1.5">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCurrentIndex(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                        i === currentIndex
                          ? 'w-7 bg-[#15803d]'
                          : 'w-2.5 bg-gray-200 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>

                {/* Arrow navigation buttons */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentIndex(
                        (prev) => (prev - 1 + testimonials.length) % testimonials.length,
                      )
                    }
                    className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#e8f8f0] hover:text-[#15803d] transition-colors text-sm font-bold cursor-pointer"
                    aria-label="Previous testimonial"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentIndex((prev) => (prev + 1) % testimonials.length)}
                    className="h-8 w-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#e8f8f0] hover:text-[#15803d] transition-colors text-sm font-bold cursor-pointer"
                    aria-label="Next testimonial"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Visual Card */}
          <div className="lg:col-span-6 rounded-2xl p-6 sm:p-9 text-white flex flex-col justify-end min-h-[320px] relative overflow-hidden border border-gray-100 shadow-xs">
            {/* Background images crossfade */}
            {testimonials.map((t, idx) => (
              <div
                key={t.image}
                className={`absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out ${
                  idx === currentIndex
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-105 pointer-events-none'
                }`}
                style={{ backgroundImage: `url("${t.image}")` }}
              />
            ))}

            {/* Dark green overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/95 via-emerald-950/70 to-black/30 z-10" />

            {/* Bottom Headline */}
            <div className="relative z-20 space-y-3">
              <AnimatePresence mode="wait">
                <motion.h3
                  key={currentIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="font-display text-2xl sm:text-3xl font-extrabold leading-tight text-white max-w-md"
                >
                  {active.headline}
                </motion.h3>
              </AnimatePresence>
              <div className="h-1 w-14 bg-emerald-400 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
